'use client'

import clsx from 'clsx'
import React, {useMemo, useState} from 'react'

import {HoverPrefetchLink} from '@/_components/nextjs/hover-prefetch-link'
import {IAnnualProgram, IMedanteCreditProgram, PROGRAM_CARD_TYPE_ENUM} from '@/_types'
import {IPreventiveExamination} from '@/_types/preventive-examination'
import { pushAddToCart } from '@/_utils/analytics/datalayer'

import AContentCardAddAdditional from './a-content-card-add-additional'

// 1:1 with pages.preventiveExaminationsCategories.overview (sk)
const ORDER_BUTTON_LABELS = {
	orderText: 'Objednať',
	orderTextLong: 'Objednať program',
	orderTextSecondary: 'Pridané extra moduly v hodnote'
} as const

interface IProps {
	children: React.ReactNode
	program: IPreventiveExamination | IAnnualProgram | IMedanteCreditProgram
	type: PROGRAM_CARD_TYPE_ENUM
	item_category2?: string
}

export const AContentCardOrderButton = ({
											className,
											program,
											selectedModules,
											extraText,
											outline = false,
											type,
	item_category2
}: {
	className?: string
	program: IPreventiveExamination | IAnnualProgram | IMedanteCreditProgram
	selectedModules: any[]
	extraText?: string
	outline?: boolean
	type: PROGRAM_CARD_TYPE_ENUM
	item_category2?: string
}) => {
	const totalPrice = selectedModules.reduce((acc, module) => acc + module.price, 0)

	const getOrderHref = (
		program: IPreventiveExamination | IAnnualProgram | IMedanteCreditProgram,
		selectedModules: any[]
	) => {
		return (
			`/volne-terminy?type=${type}&id=${program?.id}` +
			(selectedModules.length > 0
				? `&extraModules=${selectedModules.map(m => m.id).join(',')}`
				: '')
		)
	}

	const hasPriceOld = 'priceOld' in program && program.priceOld

	const priceText = useMemo(() => {
		const orderText = (hasPriceOld || type === PROGRAM_CARD_TYPE_ENUM.MEDANTE_CREDIT)
			? ORDER_BUTTON_LABELS.orderText
			: ORDER_BUTTON_LABELS.orderTextLong

		if (extraText) {
			return (
				<p className='text-sm font-semibold'>
					{orderText}
					<br/>
					{extraText} ({program.price} €
					{hasPriceOld ? (
						<span className='line-through ml-2'>{program.priceOld} €</span>
					) : (
						''
					)}
					)
				</p>
			)
		} else {
			return (
				<p className='text-sm font-semibold'>
					{orderText} {type !== PROGRAM_CARD_TYPE_ENUM.MEDANTE_CREDIT && (
					<>
						({program.price} €
						{hasPriceOld ? (
							<span className='line-through ml-2'>{program.priceOld} €</span>
						) : (
							''
						)}
						)
					</>
				)}
				</p>
			)
		}
	}, [program, extraText, hasPriceOld, type])

	const handleOrderClick = () => {
		const priceOld = 'priceOld' in program ? (program.priceOld as number | null | undefined) : undefined
		pushAddToCart(
			{ id: program.id, name: program.name, type, price: program.price, priceOld },
			{ quantity: 1, ...(item_category2 !== undefined && { item_category2 }) }
		)
	}

	return (
		<div className={clsx(className, 'item_complex-health_mid rounded-full m-4')}>
			<HoverPrefetchLink
				href={getOrderHref(program, selectedModules)}
				title={ORDER_BUTTON_LABELS.orderText}
				className={clsx(outline && 'outline')}
				onClick={handleOrderClick}
			>
				<div>
					{priceText}

					{selectedModules.length > 0 && (
						<p className='text-xs'>
							{ORDER_BUTTON_LABELS.orderTextSecondary} {totalPrice} €
						</p>
					)}
				</div>
				<span>
					<span>
						<svg className='sico'>
							<use href='/assets/layout/icons.svg#s_ar2_r'/>
						</svg>
					</span>
					<span>
						<svg className='sico'>
							<use href='/assets/layout/icons.svg#s_ar2_r'/>
						</svg>
					</span>
				</span>
			</HoverPrefetchLink>
		</div>
	)
}

const AContentCardContentWithAdditional: React.FC<IProps> = ({ children, program, type, item_category2 }) => {
	const [selectedModules, setSelectedModules] = useState<any[]>([])

	const handleModulesChange = (mod: any, isChecked: boolean) => {
		setSelectedModules(prev =>
			isChecked ? [...prev.filter(m => m.id !== mod.id), mod] : prev.filter(m => m.id !== mod.id)
		)
	}

	const hasModules = 'modules' in program
	const hasChildProgram = '_childProgram' in program

	const childButtonExtraText =
		hasChildProgram && program._childProgram
			? ' +' + program._childProgram.name?.split('+')?.[1]
			: ''

	return (
		<>
			<div className='h-full'>{children}</div>

			<div suppressHydrationWarning>
				{hasModules && (
					<div className='mx-4'>
						<AContentCardAddAdditional
							data={program}
							onModulesChange={handleModulesChange}
							selectedModuleIds={selectedModules.map(m => m.id)}
						/>
					</div>
				)}

			{hasChildProgram ? (
				<>
					<AContentCardOrderButton
						program={program}
						selectedModules={selectedModules}
						outline
						type={type}
						item_category2={item_category2}
					/>

					<AContentCardOrderButton
						program={program._childProgram as IAnnualProgram}
						selectedModules={selectedModules}
						extraText={childButtonExtraText}
						className='mt-0'
						type={type}
						item_category2={item_category2}
					/>
				</>
			) : (
				<AContentCardOrderButton
					program={program}
					selectedModules={selectedModules}
					type={type}
					item_category2={item_category2}
				/>
			)}
			</div>
		</>
	)
}

export default AContentCardContentWithAdditional
